import React from 'react';

import CalendarMonthIcon from '@mui/icons-material/CalendarMonthOutlined';
import { Box, Button, Typography } from '@mui/material';
import { Link } from 'react-router-dom';

import { tokens } from '../../theme';

// Stitch closing band: navy, label, large heading, primary and secondary actions.
export default function CallBand({
  id = 'call-band',
  label = 'Talk with the principal',
  title = 'Book a 20-minute call',
  text = 'Connect directly with technical direction to review requirements, systems, or upcoming projects.',
  secondary = { label: 'Send a message', to: '/contact-us#write-heading' },
}: {
  id?: string;
  label?: string;
  title?: string;
  text?: string;
  secondary?: { label: string; to: string };
}) {
  return (
    <Box
      component='section'
      aria-labelledby={id}
      sx={{
        mt: { xs: 6, md: 10 },
        bgcolor: tokens.navy,
        color: '#FFFFFF',
        borderRadius: 3,
        px: { xs: 3, md: 5 },
        py: { xs: 4, md: 5 },
        display: 'flex',
        flexDirection: { xs: 'column', md: 'row' },
        alignItems: { md: 'center' },
        justifyContent: 'space-between',
        gap: 3,
        backgroundImage:
          'radial-gradient(30rem 14rem at 85% 50%, rgba(76, 214, 251, 0.10), transparent 70%)',
        // The site's light-surface focus colour is too dark on navy.
        '& a:focus-visible, & .Mui-focusVisible': {
          outline: '3px solid #FFFFFF !important',
          outlineOffset: '3px !important',
        },
      }}
    >
      <Box sx={{ maxWidth: '36rem' }}>
        <Typography
          variant='labelCaps'
          component='p'
          sx={{ color: tokens.cyan }}
        >
          {label}
        </Typography>
        <Typography
          id={id}
          component='h2'
          variant='h2'
          sx={{ color: '#FFFFFF', mt: 1 }}
        >
          {title}
        </Typography>
        <Typography
          variant='bodySm'
          component='p'
          sx={{ color: tokens.onNavyMuted, mt: 1 }}
        >
          {text}
        </Typography>
      </Box>
      <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 2.5 }}>
        <Button
          component={Link}
          to='/contact-us#book'
          data-track='book_call'
          variant='contained'
          endIcon={<CalendarMonthIcon aria-hidden='true' />}
          sx={{
            bgcolor: '#FFFFFF',
            color: '#001026',
            '&:hover': { bgcolor: '#E2E7FF' },
          }}
        >
          Book a call
        </Button>
        <Box
          component={Link}
          to={secondary.to}
          sx={{
            color: tokens.cyan,
            fontWeight: 600,
            fontSize: '0.875rem',
            textUnderlineOffset: '0.25em',
          }}
        >
          {secondary.label} <span aria-hidden='true'>→</span>
        </Box>
      </Box>
    </Box>
  );
}
